import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

/**
 * Data source for the Ops → Crons switchboard: ops/cron-controls/crons, live.
 * One subscription, zero logic — merging with the catalog and the ON/OFF
 * semantics are the screen's pure helpers, not this model's business.
 *
 * True fetch → tuple, so the screen can tell "still loading" from "no crons
 * registered yet": [loading, controls, error].
 */
export function useCronControls() {
    const [state, setState] = useState({ loading: true, controls: [], error: null });

    useEffect(() => firebase.firestore()
        .collection('ops').doc('cron-controls').collection('crons')
        .onSnapshot(
            (snap) => setState({
                loading: false,
                controls: snap.docs.map((doc) => ({ name: doc.id, ...doc.data() })),
                error: null,
            }),
            (err) => {
                console.warn('[useCronControls] listen failed:', err && err.message);
                setState({ loading: false, controls: [], error: err.message || 'Could not load cron controls' });
            },
        ), []);

    return [state.loading, state.controls, state.error];
}

/**
 * ONE cron's control doc, live: [loading, control, error]. control is null
 * while loading AND when no doc exists yet (a cron that has never ticked or
 * been switched) — the screen falls back to its catalog entry.
 */
export function useCronControl(cronName) {
    const [state, setState] = useState({ loading: true, control: null, error: null });

    useEffect(() => {
        if (!cronName) { setState({ loading: false, control: null, error: null }); return undefined; }
        setState({ loading: true, control: null, error: null });
        return firebase.firestore()
            .collection('ops').doc('cron-controls').collection('crons').doc(cronName)
            .onSnapshot(
                (snap) => setState({
                    loading: false,
                    control: snap.exists ? { name: snap.id, ...snap.data() } : null,
                    error: null,
                }),
                (err) => {
                    console.warn('[useCronControl] listen failed:', err && err.message);
                    setState({ loading: false, control: null, error: err.message || 'Could not load the cron' });
                },
            );
    }, [cronName]);

    return [state.loading, state.control, state.error];
}

/** A cron's switch history (newest first, capped at 100), live: [loading, entries, error]. */
export function useCronHistory(cronName) {
    const [state, setState] = useState({ loading: true, entries: [], error: null });

    useEffect(() => {
        if (!cronName) { setState({ loading: false, entries: [], error: null }); return undefined; }
        setState({ loading: true, entries: [], error: null });
        return firebase.firestore()
            .collection('ops').doc('cron-controls').collection('crons').doc(cronName)
            .collection('history').orderBy('at', 'desc').limit(100)
            .onSnapshot(
                (snap) => setState({
                    loading: false,
                    entries: snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
                    error: null,
                }),
                (err) => {
                    console.warn('[useCronHistory] listen failed:', err && err.message);
                    setState({ loading: false, entries: [], error: err.message || 'Could not load history' });
                },
            );
    }, [cronName]);

    return [state.loading, state.entries, state.error];
}

/**
 * The switch action: plain async function, no hooks. ops/** is
 * browser-unwritable by design — the admin callable is the only write path,
 * and it stamps who flipped what, when. Callers re-render from the
 * subscription's next snapshot, never from a local echo of this write.
 */
export function setCronEnabled(cronName, enabled) {
    return firebase.functions()
        .httpsCallable('userService-cronsHandler-adminSetCronEnabled')({ cronName, enabled });
}
